// Picking the drop-off location on a new request has to respect the equipment.
// When a machine is selected, the `jobSites` list comes back filtered to the
// locations that machine belongs to (Zachry's SAP project, `Gf.Zachry.LocationFilter`).
// The job site is the default ship-to, but not the only legal one: a user may ship
// to any location of the org, a yard or a shop included (GEA-11323). So the job
// sites lead the list and supply the default; every other location follows.
// GEA-7296.

type LocationLike = { id: string }

/**
 * The page size the `CreateRequestSelectLocations` query asks for. Keep this in
 * sync with `pageSize` in
 * `SelectVendorsStep/MyVendors/CreateRequestSelectLocations.graphql` — that
 * literal is what the server actually caps the list at.
 */
export const LOCATIONS_PAGE_SIZE = 20

/**
 * The location to preselect. With a machine selected, the default shipping
 * location is used only if it is one of the machine's job sites; otherwise the
 * first job site wins.
 */
export const pickDefaultLocation = <T extends LocationLike>(
  jobSites: readonly T[],
  defaultShippingLocation: T | null | undefined,
  equipmentFiltered: boolean
): T | undefined => {
  if (!equipmentFiltered) return defaultShippingLocation ?? jobSites[0]

  return jobSites.find((location) => location.id === defaultShippingLocation?.id) ?? jobSites[0]
}

/**
 * The list to show: the default first, then the machine's other job sites, then
 * every other location. Each location appears once.
 */
export const jobSitesFirst = <T extends LocationLike>(
  locations: readonly T[],
  jobSites: readonly T[],
  defaultLocation: T | null | undefined
): T[] => {
  const ordered = [...(defaultLocation ? [defaultLocation] : []), ...jobSites, ...locations]
  const seen = new Set<string>()

  return ordered.filter((location) => {
    if (seen.has(location.id)) return false
    seen.add(location.id)
    return true
  })
}

/**
 * Whether a selected location is one of the machine's job sites. It decides
 * whether a new machine moves the selection to that machine's job site.
 *
 * Only a complete list proves a location is not a job site. A list at the page
 * cap may be truncated, so treat it as inconclusive and keep the selection.
 */
export const isJobSiteOrUnknown = (
  jobSites: readonly LocationLike[],
  locationId: string | null | undefined,
  equipmentFiltered: boolean
): boolean => {
  if (!equipmentFiltered || !locationId) return true
  if (jobSites.length >= LOCATIONS_PAGE_SIZE) return true

  return jobSites.some((location) => location.id === locationId)
}
